import {
  Activity,
  Banknote,
  BellRing,
  Calculator,
  Globe2,
  WifiOff,
  type LucideIcon,
} from 'lucide-react';

interface Feature {
  icon: LucideIcon;
  title: string;
  body: string;
}

const FEATURES: Feature[] = [
  {
    icon: Activity,
    title: 'Live cash rates',
    body:
      'Real-time LYD quotes for USD, EUR, GBP, TRY, EGP, TND, SAR, and AED — refreshed every minute.',
  },
  {
    icon: Banknote,
    title: 'Per-bank rates',
    body:
      'See the rate every Libyan bank actually quotes today, side by side. No more guessing which counter to visit.',
  },
  {
    icon: BellRing,
    title: 'Smart alerts',
    body:
      'Get pinged when a currency crosses a price you choose, or moves by a % you care about. Cash or bank rates.',
  },
  {
    icon: Calculator,
    title: 'Built-in converter',
    body:
      'A fast, beautiful converter that uses the same live rates — and lets you pick which bank to convert against.',
  },
  {
    icon: Globe2,
    title: 'English · العربية · Türkçe',
    body:
      'Full localization with RTL support. The whole app — including alerts and notifications — speaks your language.',
  },
  {
    icon: WifiOff,
    title: 'Offline-first',
    body:
      'Rates and history are cached locally. The app stays useful on a slow or dropped connection.',
  },
];

export default function Features() {
  return (
    <section id="features" className="relative scroll-mt-24 py-24 sm:py-32">
      <div className="container-page">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-cyan">
            Everything you need
          </p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Built for the way Libyans actually trade currency.
          </h2>
          <p className="mt-4 text-base text-slate-400 sm:text-lg">
            Dinar Pulse pulls quotes from Fulus.ly and every major Libyan bank
            so you always see the rate that matters — wherever you are.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="glass-card group relative overflow-hidden p-6 transition-colors hover:border-brand-cyan/40"
            >
              <div className="absolute inset-0 -z-10 bg-card-glow opacity-0 transition-opacity group-hover:opacity-100" />
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-brand-cyan/30 bg-brand-cyan/10 text-brand-cyan">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-white">
                {title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
